import type { ReactNode } from 'react';
import { TwDataTable, type DataTableColumn } from '../components/TwDataTable';

/**
 * Ant Design `Table` → Tailwind adapter. Maps the common antd Table API onto the
 * shared `TwDataTable`, so `import { Table } from 'antd'` becomes `from '../../ui'`
 * with (mostly) no prop changes. Consolidates on ONE table system.
 *
 * Supported: columns (title/dataIndex/key/render/width/align/ellipsis/sorter),
 * dataSource, rowKey, loading, pagination (object|false), expandable, onRow,
 * rowClassName, size, scroll (horizontal handled automatically), bordered.
 * Not (yet) supported: rowSelection, fixed columns, filters, onChange sorting —
 * migrate those pages by hand (rare).
 */
export interface AntdColumn<T = any> {
  title?: ReactNode;
  dataIndex?: string | string[];
  key?: string;
  render?: (value: any, record: T, index: number) => ReactNode;
  width?: number | string;
  align?: 'left' | 'center' | 'right';
  ellipsis?: boolean;
  sorter?: boolean | ((a: T, b: T) => number);
  fixed?: 'left' | 'right' | boolean;
  responsive?: string[];
  defaultVisible?: boolean;
  alwaysVisible?: boolean;
  // ignored: onCell, filters, filterDropdown, etc.
  [k: string]: unknown;
}

export interface AntdTableProps<T = any> {
  columns?: AntdColumn<T>[];
  dataSource?: T[];
  rowKey?: string | ((r: T, index?: number) => string);
  loading?: boolean;
  pagination?: false | { pageSize?: number; defaultPageSize?: number; showSizeChanger?: boolean; pageSizeOptions?: (number | string)[]; current?: number; total?: number; hideOnSinglePage?: boolean; showTotal?: (total: number, range: [number, number]) => ReactNode; size?: 'small' | 'default'; position?: string[]; simple?: boolean; onChange?: (page: number, pageSize: number) => void };
  expandable?: {
    expandedRowRender?: (r: T) => ReactNode;
    rowExpandable?: (r: T) => boolean;
    expandRowByClick?: boolean;
    expandedRowKeys?: (string | number)[];
    onExpandedRowsChange?: (keys: (string | number)[]) => void;
    childrenColumnName?: string;
    /** antd compat (accepted; the ui table always renders its own chevron toggle column). */
    showExpandColumn?: boolean;
    defaultExpandAllRows?: boolean;
    defaultExpandedRowKeys?: (string | number)[];
  };
  rowSelection?: {
    selectedRowKeys?: (string | number)[];
    onChange?: (selectedKeys: (string | number)[], selectedRows: T[]) => void;
    type?: 'checkbox' | 'radio';
    getCheckboxProps?: (r: T) => { disabled?: boolean };
  };
  onRow?: (record: T, index?: number) => { onClick?: (e?: any) => void; style?: React.CSSProperties; className?: string } | undefined;
  rowClassName?: (record: T, index: number) => string;
  /** antd footer summary — `(pageData) => <Table.Summary.Row>…</Table.Summary.Row>`. */
  summary?: (data: T[]) => ReactNode;
  size?: 'small' | 'middle' | 'large';
  scroll?: { x?: number | string | true; y?: number | string };
  bordered?: boolean;
  className?: string;
  /** Persist column-visibility/density under this key + show the table toolbar. */
  storageKey?: string;
  /** `table-layout: fixed` — shares leftover width per the columns' own widths. */
  layoutFixed?: boolean;
  /** Sizes each column to its content (heading included) and shares the leftover width. */
  autoFit?: boolean;
  showToolbar?: boolean;
  locale?: { emptyText?: ReactNode };
  title?: () => ReactNode;
  footer?: () => ReactNode;
  /** Controlled SERVER-side sort — forwarded to TwDataTable (see useServerSort). */
  sortState?: { key: string; dir: 'asc' | 'desc' } | null;
  onSortChange?: (next: { key: string; dir: 'asc' | 'desc' } | null) => void;
}

function hashCols(cols: AntdColumn[]): string {
  const s = cols.map((c) => c.key ?? (Array.isArray(c.dataIndex) ? c.dataIndex.join('.') : c.dataIndex) ?? (typeof c.title === 'string' ? c.title : '')).join('|');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return 'antd-' + Math.abs(h).toString(36);
}

function TableBase<T extends Record<string, any>>({
  columns = [], dataSource = [], rowKey = 'key', loading, pagination, expandable, rowSelection, onRow, rowClassName, summary,
  bordered = true, className, storageKey, showToolbar, locale, title, footer, sortState, onSortChange,
  layoutFixed, autoFit,
}: AntdTableProps<T>) {
  const twCols: DataTableColumn<T>[] = columns.map((c) => ({
    key: c.key ?? (typeof c.dataIndex === 'string' ? c.dataIndex : undefined),
    title: c.title,
    dataIndex: c.dataIndex,
    render: c.render,
    width: c.width as number | undefined,
    align: c.align,
    ellipsis: c.ellipsis,
    sortable: !!c.sorter,
    sorter: typeof c.sorter === 'function' ? c.sorter : undefined,
    defaultVisible: c.defaultVisible,
    alwaysVisible: c.alwaysVisible,
  }));

  const pg = pagination === false
    ? false
    : {
        pageSize: pagination?.pageSize ?? pagination?.defaultPageSize ?? 10,
        showSizeChanger: pagination?.showSizeChanger,
        pageSizeOptions: pagination?.pageSizeOptions?.map(Number),
        hideOnSinglePage: pagination?.hideOnSinglePage,
        showTotal: pagination?.showTotal,
        size: pagination?.size,
        // Controlled / server-side pagination — forwarded so the caller drives page + total.
        current: pagination?.current,
        total: pagination?.total,
        onChange: pagination?.onChange,
      };

  // Default to NO toolbar unless a storageKey is given (list pages opt in); keeps modal/inline tables clean.
  const withToolbar = showToolbar ?? !!storageKey;

  return (
    <div className={className}>
      {title && <div className="mb-3 type-body-strong text-content">{title()}</div>}
      <TwDataTable<T>
        storageKey={storageKey ?? hashCols(columns)}
        columns={twCols}
        dataSource={dataSource}
        rowKey={rowKey}
        loading={loading}
        pagination={pg}
        layoutFixed={layoutFixed}
        autoFit={autoFit}
        expandable={expandable as any}
        rowSelection={rowSelection as any}
        onRow={onRow as any}
        rowClassName={rowClassName ? (r) => rowClassName(r, 0) : undefined}
        summary={summary as any}
        emptyText={locale?.emptyText}
        showToolbar={withToolbar}
        bordered={bordered}
        sortState={sortState}
        onSortChange={onSortChange}
      />
      {footer && <div className="mt-3 text-sm text-muted">{footer()}</div>}
    </div>
  );
}

// ── antd Table.Summary.Row / Table.Summary.Cell (footer totals) ──
function SummaryRow({ children, className }: { children?: ReactNode; className?: string }) {
  return <tr className={className}>{children}</tr>;
}
function SummaryCell({ children, colSpan, align, className }: { children?: ReactNode; index?: number; colSpan?: number; align?: 'left' | 'center' | 'right'; className?: string }) {
  return (
    <td colSpan={colSpan} className={`px-3 py-2.5 align-middle ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'} ${className ?? ''}`}>
      {children}
    </td>
  );
}
const Summary = Object.assign(function Summary({ children }: { children?: ReactNode }) { return <>{children}</>; }, { Row: SummaryRow, Cell: SummaryCell });

/** Exported as `Table`, with `Table.Summary` (antd-compatible static). */
export const Table = Object.assign(TableBase, { Summary });
